import React, { FC, lazy, useEffect } from "react";
import {
  Navigate,
  Route,
  RouterProvider,
  createBrowserRouter,
  createRoutesFromElements,
} from "react-router";

import useNotifier from "../hooks/use-notifier";
import { AuthGuard } from "../permissions/auth-guard";
import { NeedsOnboarding } from "../permissions/needs-onboarding";
import { NotLoggedInGuard } from "../permissions/not-logged-in";
import { setHelpDocs } from "../slices/help_docs";
import { useDispatch } from "../store";
import { Logout } from "./firebase/logout";

const Logs = lazy(() => import("./logs"));
const Traffic = lazy(() => import("./traffic"));
const Usage = lazy(() => import("./usage"));

const NotFoundView = lazy(() => import("./errors/NotFoundView"));
const DashboardLayout = lazy(() => import("../layouts/dashboard"));
const EnvoyRoutes = lazy(() => import("../views/routes"));
const Policies = lazy(() => import("../views/policies"));
const Certificates = lazy(() => import("../views/certificates"));
const ClusterSettings = lazy(() => import("../views/settings"));
const Login = lazy(() => import("./firebase/login"));
const CreateAccount = lazy(() => import("./firebase/createAccount"));
const VerifyEmail = lazy(() => import("./firebase/verifyEmail"));
const Onboarding = lazy(() => import("../views/onboarding"));
const Organizations = lazy(() => import("../views/organizations"));
const Clusters = lazy(() => import("../views/clusters"));
const Account = lazy(() => import("../views/account"));
const Deployments = lazy(() => import("../views/deployments"));
const ServiceAccounts = lazy(() => import("../views/serviceAccounts"));
const Status = lazy(() => import("../views/status"));
const ApiTokens = lazy(() => import("../views/apiTokens"));
const Billing = lazy(() => import("../views/billing"));
const FirebaseHandlers = lazy(() => import("../views/firebase/handlers"));
const OrgAccessDenied = lazy(() => import("../views/errors/OrgAccessDenied"));
const ApiDocs = lazy(() => import("../components/ApiDocs"));
const Enterprise = lazy(() => import("../views/enterprise"));

const Router: FC = () => {
  const dispatch = useDispatch();
  useNotifier();

  useEffect(() => {
    dispatch(setHelpDocs());
  }, [dispatch]);

  const router = createBrowserRouter(
    createRoutesFromElements(
      <>
        <Route path="/handlers" element={<FirebaseHandlers />} />
        <Route element={<NotLoggedInGuard />}>
          <Route path="/" element={<Navigate to={"/login"} />} />
          <Route path="/login" element={<Login />} />
          <Route path="/create-account" element={<CreateAccount />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
        </Route>
        <Route element={<AuthGuard />}>
          <Route path="/logout" element={<Logout />} />
          <Route path="/onboarding" element={<Onboarding />} />
          <Route path="/enterprise/*" element={<Enterprise />} />
          <Route element={<NeedsOnboarding />}>
            <Route path="/openapi" element={<ApiDocs />} />
            <Route path="/app/*" element={<DashboardLayout />}>
              <Route path="org-access-denied" element={<OrgAccessDenied />} />
              <Route path="clusters/*" element={<Clusters />} />
              <Route path="account" element={<Account />} />
              <Route path="billing/*" element={<Billing />} />
              <Route path="organizations/*" element={<Organizations />} />
              <Route path="management">
                <Route path="policies/*" element={<Policies />} />
                <Route path="routes/*" element={<EnvoyRoutes />} />
                <Route path="certificates/*" element={<Certificates />} />
                <Route path="api-tokens/*" element={<ApiTokens />} />
                <Route path="service-accounts/*" element={<ServiceAccounts />} />
                <Route path="*" element={<NotFoundView />} />
              </Route>
              <Route path="settings/*" element={<ClusterSettings />} />
              <Route path="reports">
                <Route path="status/*" element={<Status />} />
                <Route path="deployments/*" element={<Deployments />} />
                <Route path="usage/*" element={<Usage />} />
                <Route path="traffic/*" element={<Traffic />} />
                <Route path="logs/*" element={<Logs />} />
                <Route path="*" element={<NotFoundView />} />
              </Route>
              <Route path="*" element={<NotFoundView />} />
            </Route>
          </Route>
        </Route>
        <Route path="*" element={<NotFoundView />} />
      </>,
    ),
  );

  return <RouterProvider router={router} />;
};

export default Router;
